import { useState, useEffect, useCallback, useRef } from 'react';

// ============================================================================
// TIPOS
// ============================================================================

export interface ServiceWorkerUpdateStatus {
  isUpdateAvailable: boolean;
  isUpdating: boolean;
  currentVersion: string | null;
  newVersion: string | null;
  isRegistered: boolean;
  registration: ServiceWorkerRegistration | null;
  error: string | null;
}

export interface ServiceWorkerUpdateOptions {
  checkInterval?: number; // Intervalo para verificar actualizaciones en ms (default: 60000)
  enableAutoCheck?: boolean; // Habilitar verificación automática (default: true)
  onUpdateFound?: (newVersion: string) => void; // Callback cuando se encuentra actualización
  onUpdateInstalled?: () => void; // Callback cuando se instala actualización
  onError?: (error: Error) => void; // Callback de error
}

export interface ServiceWorkerUpdateResult extends ServiceWorkerUpdateStatus {
  checkForUpdate: () => Promise<void>;
  applyUpdate: () => void;
  skipUpdate: () => void;
  dismissUpdate: () => void;
}

interface ServiceWorkerMessage {
  type: string;
  version?: string;
  cacheName?: string;
}

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

export function useServiceWorkerUpdate(
  options: ServiceWorkerUpdateOptions = {}
): ServiceWorkerUpdateResult {
  const {
    checkInterval = 60000, // 60 segundos
    enableAutoCheck = true,
    onUpdateFound,
    onUpdateInstalled,
    onError
  } = options;

  // Estados principales
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Referencias
  const checkTimeoutRef = useRef<NodeJS.Timeout>();
  const hasNotifiedUpdate = useRef(false);

  // ============================================================================
  // FUNCIONES
  // ============================================================================

  /**
   * Registrar el Service Worker si no está registrado
   */
  const registerServiceWorker = useCallback(async () => {
    // No registrar el service worker en desarrollo para evitar problemas de caché con HMR
    if (import.meta.env.DEV) {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
          console.log('Service Worker desregistrado automáticamente en modo desarrollo.');
        }
      }
      return null;
    }

    if (!('serviceWorker' in navigator)) {
      const errorMsg = 'Service Worker no está soportado en este navegador';
      console.warn(errorMsg);
      setError(errorMsg);
      return null;
    }

    try {
      const reg = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none' // Evitar caché del Service Worker mismo
      });

      console.log('Service Worker registrado:', reg.scope);
      setIsRegistered(true);
      setRegistration(reg);
      setError(null);

      // Obtener versión actual si existe
      if (reg.active) {
        // La versión se comunicará mediante mensaje
        reg.active.postMessage({ type: 'GET_VERSION' });
      }

      return reg;
    } catch (err) {
      const errorMsg = `Error registrando Service Worker: ${err instanceof Error ? err.message : String(err)}`;
      console.error(errorMsg);
      setError(errorMsg);
      if (onError) {
        onError(err instanceof Error ? err : new Error(String(err)));
      }
      return null;
    }
  }, [onError]);

  /**
   * Verificar si hay actualizaciones disponibles
   */
  const checkForUpdate = useCallback(async () => {
    if (!registration) {
      console.log('No hay registro de Service Worker, intentando registrar...');
      await registerServiceWorker();
      return;
    }

    try {
      console.log('Verificando actualizaciones del Service Worker...');
      await registration.update();
    } catch (err) {
      const errorMsg = `Error verificando actualizaciones: ${err instanceof Error ? err.message : String(err)}`;
      console.error(errorMsg);
      setError(errorMsg);
      if (onError) {
        onError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  }, [registration, registerServiceWorker, onError]);

  /**
   * Aplicar la actualización (recargar la página)
   */
  const applyUpdate = useCallback(() => {
    if (!isUpdateAvailable) {
      console.warn('No hay actualización disponible para aplicar');
      return;
    }

    console.log('Aplicando actualización...');
    setIsUpdating(true);

    // Si hay un Service Worker en espera, activarlo
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Recargar después de un breve delay para asegurar que se active
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } else {
      // Si no hay SW en espera, simplemente recargar
      window.location.reload();
    }
  }, [isUpdateAvailable, registration]);

  /**
   * Omitir esta actualización (no mostrar notificación de nuevo)
   */
  const skipUpdate = useCallback(() => {
    console.log('Omitiendo actualización');
    setIsUpdateAvailable(false);
    setNewVersion(null);
    hasNotifiedUpdate.current = false;
  }, []);

  /**
   * Descartar la notificación de actualización (pero mantener el estado)
   */
  const dismissUpdate = useCallback(() => {
    console.log('Descartando notificación de actualización');
    hasNotifiedUpdate.current = false;
  }, []);

  // ============================================================================
  // EFECTOS
  // ============================================================================

  /**
   * Registrar Service Worker al montar
   */
  useEffect(() => {
    registerServiceWorker();
  }, [registerServiceWorker]);

  /**
   * Escuchar mensajes del Service Worker
   */
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event: MessageEvent<ServiceWorkerMessage>) => {
      const { data } = event;
      
      console.log('Mensaje recibido del Service Worker:', data);

      if (data.type === 'SW_UPDATED_AUTO_RELOAD') {
        // Actualización automática: recargar la página inmediatamente
        const version = data.version || 'unknown';
        console.log('🔄 Nueva versión disponible, recargando automáticamente...', version);
        
        // Esperar un breve momento para que el SW termine de activarse
        setTimeout(() => {
          window.location.reload();
        }, 500);
        
      } else if (data.type === 'SW_UPDATED') {
        const version = data.version || 'unknown';
        console.log('Nueva versión del Service Worker disponible:', version);
        
        setNewVersion(version);
        setIsUpdateAvailable(true);
        
        // Notificar solo una vez
        if (!hasNotifiedUpdate.current) {
          hasNotifiedUpdate.current = true;
          
          if (onUpdateFound) {
            onUpdateFound(version);
          }
          
          if (onUpdateInstalled) {
            onUpdateInstalled();
          }
        }
      } else if (data.type === 'VERSION_INFO') {
        // Respuesta a GET_VERSION
        const version = data.version || 'unknown';
        console.log('Versión actual del Service Worker:', version);
        setCurrentVersion(version);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [onUpdateFound, onUpdateInstalled]);

  /**
   * Detectar cuando un nuevo Service Worker está instalado y en espera
   */
  useEffect(() => {
    if (!registration) return;

    const handleUpdateFound = () => {
      console.log('Service Worker: Update encontrado');
      
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        console.log('Service Worker: Estado cambió a', newWorker.state);
        
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // Hay una nueva versión disponible
          console.log('Nueva versión del Service Worker instalada y lista');
          
          // El mensaje SW_UPDATED será manejado por el listener anterior
          // Aquí solo registramos el evento
        }
      });
    };

    registration.addEventListener('updatefound', handleUpdateFound);

    return () => {
      registration.removeEventListener('updatefound', handleUpdateFound);
    };
  }, [registration]);

  /**
   * Detectar cuando el Service Worker controlador cambia
   */
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleControllerChange = () => {
      console.log('Service Worker: Controlador cambió');
      // El controlador cambió, probablemente después de skipWaiting
      // La página se recargará automáticamente en applyUpdate
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  /**
   * Verificación automática de actualizaciones
   */
  useEffect(() => {
    if (!enableAutoCheck || !registration) return;

    // Verificar inmediatamente
    checkForUpdate();

    // Configurar verificación periódica
    checkTimeoutRef.current = setInterval(() => {
      checkForUpdate();
    }, checkInterval);

    return () => {
      if (checkTimeoutRef.current) {
        clearInterval(checkTimeoutRef.current);
      }
    };
  }, [enableAutoCheck, checkInterval, registration, checkForUpdate]);

  /**
   * Verificar actualización cuando la ventana recupera el foco
   */
  useEffect(() => {
    if (!enableAutoCheck || !registration) return;

    const handleFocus = () => {
      console.log('Ventana enfocada, verificando actualizaciones...');
      checkForUpdate();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [enableAutoCheck, registration, checkForUpdate]);

  /**
   * Limpiar timers al desmontar
   */
  useEffect(() => {
    return () => {
      if (checkTimeoutRef.current) {
        clearInterval(checkTimeoutRef.current);
      }
    };
  }, []);

  // ============================================================================
  // RESULTADO
  // ============================================================================

  return {
    isUpdateAvailable,
    isUpdating,
    currentVersion,
    newVersion,
    isRegistered,
    registration,
    error,
    checkForUpdate,
    applyUpdate,
    skipUpdate,
    dismissUpdate
  };
}

export default useServiceWorkerUpdate;

